import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { PageHeader } from "../components/Common";
import { ArrowUp, ArrowDown, Equals } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const nf = (v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v);

export default function CashFlow() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="p-8 text-zinc-500">Yükleniyor...</div>;

  let running = 0;
  const cumulative = d.trend.map((t) => { running += t.net; return { month: t.month, net: t.net, birikimli: running }; });

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Nakit Akışı" subtitle="Aylık giriş, çıkış ve birikimli nakit pozisyonu" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 animate-fade-up">
        <Card label="Nakit Girişi" value={fmtTRY(d.total_income)} icon={ArrowUp} cls="text-green-600" />
        <Card label="Nakit Çıkışı" value={fmtTRY(d.total_expense + d.monthly_installments)} icon={ArrowDown} cls="text-red-600" />
        <Card label="Net Nakit Pozisyon" value={fmtTRY(d.cash_position)} icon={Equals} cls="text-[#002FA7]" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-4 animate-fade-up" data-testid="cashflow-chart">
        <h3 className="font-display text-lg font-semibold text-zinc-900 mb-1">Birikimli Nakit Akışı</h3>
        <p className="text-xs text-zinc-500 mb-4">Son 6 ay</p>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={cumulative}>
            <defs>
              <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#002FA7" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={nf} tick={{ fontSize: 11, fill: "#a1a1aa", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
            <Area type="monotone" dataKey="birikimli" name="Birikimli" stroke="#002FA7" strokeWidth={2.5} fill="url(#cf)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="cashflow-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Ay</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Gelir</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Gider</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Net</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Birikimli</th>
            </tr>
          </thead>
          <tbody>
            {d.trend.map((t, i) => (
              <tr key={t.month} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3 font-medium text-zinc-900">{t.month}</td>
                <td className="px-4 py-3 text-right font-mono text-green-600 tabular-nums">{fmtTRY(t.gelir)}</td>
                <td className="px-4 py-3 text-right font-mono text-red-600 tabular-nums">{fmtTRY(t.gider)}</td>
                <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${t.net >= 0 ? "text-zinc-900" : "text-red-600"}`}>{fmtTRY(t.net)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[#002FA7] tabular-nums">{fmtTRY(cumulative[i].birikimli)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, icon: Icon, cls }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6 hover:-translate-y-px hover:shadow-sm transition-all duration-200">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</span>
        <Icon size={18} weight="bold" className={cls} />
      </div>
      <div className={`font-mono text-2xl font-semibold mt-3 tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
